import type { Chain, ChainId } from "./types";

const ETH: Chain["nativeCurrency"] = {
  name: "Ether",
  symbol: "ETH",
  decimals: 18,
};

export const chains = {
  ethereum: {
    id: 1,
    name: "Ethereum",
    caip2: "eip155:1",
    rpcUrl: "https://ethereum-rpc.publicnode.com",
    blockExplorerUrl: "https://etherscan.io",
    nativeCurrency: ETH,
  },
  optimism: {
    id: 10,
    name: "Optimism",
    caip2: "eip155:10",
    rpcUrl: "https://optimism-rpc.publicnode.com",
    blockExplorerUrl: "https://optimistic.etherscan.io",
    nativeCurrency: ETH,
  },
  bnb: {
    id: 56,
    name: "BNB Chain",
    caip2: "eip155:56",
    rpcUrl: "https://bsc-rpc.publicnode.com",
    blockExplorerUrl: "https://bscscan.com",
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
  },
  unichain: {
    id: 130,
    name: "Unichain",
    caip2: "eip155:130",
    rpcUrl: "https://mainnet.unichain.org",
    blockExplorerUrl: "https://uniscan.xyz",
    nativeCurrency: ETH,
  },
  polygon: {
    id: 137,
    name: "Polygon",
    caip2: "eip155:137",
    rpcUrl: "https://polygon-bor-rpc.publicnode.com",
    blockExplorerUrl: "https://polygonscan.com",
    nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
  },
  zksync: {
    id: 324,
    name: "zkSync Era",
    caip2: "eip155:324",
    rpcUrl: "https://mainnet.era.zksync.io",
    blockExplorerUrl: "https://era.zksync.network",
    nativeCurrency: ETH,
  },
  base: {
    id: 8453,
    name: "Base",
    caip2: "eip155:8453",
    rpcUrl: "https://base-rpc.publicnode.com",
    blockExplorerUrl: "https://basescan.org",
    nativeCurrency: ETH,
  },
  anvil: {
    id: 31337,
    name: "Anvil",
    caip2: "eip155:31337",
    rpcUrl: "http://127.0.0.1:8545",
    blockExplorerUrl: "",
    nativeCurrency: ETH,
  },
  arbitrum: {
    id: 42161,
    name: "Arbitrum One",
    caip2: "eip155:42161",
    rpcUrl: "https://arbitrum-one-rpc.publicnode.com",
    blockExplorerUrl: "https://arbiscan.io",
    nativeCurrency: ETH,
  },
  celo: {
    id: 42220,
    name: "Celo",
    caip2: "eip155:42220",
    rpcUrl: "https://forno.celo.org",
    blockExplorerUrl: "https://celoscan.io",
    nativeCurrency: { name: "CELO", symbol: "CELO", decimals: 18 },
  },
  avalanche: {
    id: 43114,
    name: "Avalanche",
    caip2: "eip155:43114",
    rpcUrl: "https://avalanche-c-chain-rpc.publicnode.com",
    blockExplorerUrl: "https://snowtrace.io",
    nativeCurrency: { name: "AVAX", symbol: "AVAX", decimals: 18 },
  },
  blast: {
    id: 81457,
    name: "Blast",
    caip2: "eip155:81457",
    rpcUrl: "https://rpc.blast.io",
    blockExplorerUrl: "https://blastscan.io",
    nativeCurrency: ETH,
  },
  sepolia: {
    id: 11155111,
    name: "Sepolia",
    caip2: "eip155:11155111",
    rpcUrl: "https://ethereum-sepolia-rpc.publicnode.com",
    blockExplorerUrl: "https://sepolia.etherscan.io",
    nativeCurrency: ETH,
  },
} as const satisfies Record<string, Chain>;

/** Base chain ID — convenience constant for the primary L2 target chain. */
export const BASE_CHAIN_ID = 8453 as ChainId;

const byId = new Map<ChainId, Chain>();
const byCaip2 = new Map<string, Chain>();
const byName = new Map<string, Chain>();

for (const chain of Object.values(chains)) {
  byId.set(chain.id, chain);
  byCaip2.set(chain.caip2, chain);
  byName.set(chain.name.toLowerCase(), chain);
}

export function getChainById(id: ChainId): Chain | undefined {
  return byId.get(id);
}

export function getChainByCaip2(caip2: string): Chain | undefined {
  return byCaip2.get(caip2);
}

export function getChainByName(name: string): Chain | undefined {
  return byName.get(name.toLowerCase());
}
